/* Suggested path: frontend/src/pages/Dashboard.jsx
   ====================================================================
   RECONCILIATION NOTES

   This supersedes the Dashboard.jsx from the previous rebuild pass.
   That version introduced its own constants/skills.js and
   components/skill-cards/*.jsx — which duplicated (with a different,
   incompatible prop interface) the config.*.js + registry system that
   was already designed back in May and never finished. Retire both
   constants/skills.js and components/skill-cards/ — this file no
   longer imports them.

   What this version does instead:
   - Fetches skill rows from Supabase via the shared useSkills() hook
     (hooks/useSkills.js), matching the real skills_rows.json schema.
   - Resolves each live skill's card component via getSkillCard(skill.card_shape)
     from data/configs/index.js — the registry that was Task 2 of the
     original Kimi handoff and never got built.
   - Copywriting's admin-only gate is kept as app-level logic layered
     on top of the fetch (the DB row itself says status:'live' with no
     admin flag) — flagging that assumption rather than silently
     dropping the gate or silently keeping it.
   - The DB doesn't have a distinct "jump into practice" route separate
     from the skill's landing route (`route` and `landing_route` are
     identical in your export) — kept a small local EXERCISE_ROUTES map
     for that specific gap. Worth adding a real column later if you
     want this fully data-driven too.
   - Shell/streak-hero/responsive-grid/reduced-motion work from the
     previous pass is unchanged — only the skill-sourcing mechanism
     changed.

   UPDATED (this pass):
   - Removed "Vote for next skill" (VoteCard) and comingSoonSkills block.
   - Precision Storytelling now appears in the live grid with its mascot.
*/
import { useAuth } from '../contexts/AuthContext';
import { useStreak } from '../hooks/useStreak';
import { useProgress } from '../hooks/useProgress';
import { useSkills } from '@hooks/useSkills';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '@hooks/usePageTitle';
import { ArrowRight, Clock, X, BarChart3 } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { supabase } from '@services/api.js';
import ContourBG from '@components/ContourBG';
import SkillLoadingScreen from '@components/SkillLoadingScreen';
import { getSkillCard } from '@data/configs/index.js';
import VoteForNextSection from '@components/VoteForNextSection';
import MasonryGrid from '@components/MasonryGrid';

/* ─── Admin (client-side gate only) ───
   The Supabase `skills` row for copywriting says status:'live' with no
   admin flag — this gate is an app-level policy on top of that, kept
   as-is from the original code. Flag if copywriting should now be
   open to everyone since the DB itself doesn't restrict it. */
const ADMIN_EMAIL = 'plethora1000@gmail.com';
const isAdmin = (email) => email?.toLowerCase() === ADMIN_EMAIL;

/* Unfinished skills hidden from non-admins in the live grid. AI Prompting is the
   only fully public skill. (precision-storytelling is still status:'coming-soon',
   so it's not in the live grid today — this keeps it admin-only if/when flipped.) */
const ADMIN_ONLY_SKILL_IDS = new Set(['copywriting', 'sales-negotiation', 'precision-storytelling']);

/* Practice/exercise deep-links — not in the skills table yet (route and
   landing_route are identical for every row today). Falls back to the
   skill's own `route` if a skill isn't listed here. */
const EXERCISE_ROUTES = {
  // v2: was '/skills/ai-prompting/exercise/1' — missing the /lessons/:lessonId
  // segment the real route always required, so this already 404'd before
  // the v2 rep/reps rename; fixed to the same lesson-1/position-1
  // fallback the sandbox itself uses when there's no smarter deep link
  // available from here (Dashboard has no per-user rep position handy).
  'ai-prompting': '/skills/ai-prompting/lessons/1/reps/1',
  'sales-negotiation': '/voice-room',
  copywriting: '/skills/copywriting/headline-duel',
};

/* ─── Tokens ─── */
const T = { radius: { sm: 8, md: 12, lg: 16, xl: 20, '2xl': 24 } };

/* ─── Social platforms (unchanged) ─── */
const SOCIALS = [
  {
    id: 'x', label: 'X', bg: '#000', color: '#fff',
    icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>,
    url: (t) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(t)}`,
  },
  {
    id: 'linkedin', label: 'LinkedIn', bg: '#0a66c2', color: '#fff',
    icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>,
    url: (t) => `https://www.linkedin.com/sharing/share-offsite/?url=https://castleatlas.co&summary=${encodeURIComponent(t)}`,
  },
  {
    id: 'whatsapp', label: 'WhatsApp', bg: '#25d366', color: '#fff',
    icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/></svg>,
    url: (t) => `https://wa.me/?text=${encodeURIComponent(t)}`,
  },
  {
    id: 'facebook', label: 'Facebook', bg: '#1877f2', color: '#fff',
    icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>,
    url: (t) => `https://www.facebook.com/sharer/sharer.php?u=https://castleatlas.co&quote=${encodeURIComponent(t)}`,
  },
];

/* ─── Flame ─── */
const FlameIcon = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 28 28" fill="none">
    <path d="M14 2C14 2 8 8 8 14C8 17.5 10.5 20 14 20C17.5 20 20 17.5 20 14C20 8 14 2 14 2Z" fill="#ea580c">
      <animate attributeName="d" values="M14 2C14 2 8 8 8 14C8 17.5 10.5 20 14 20C17.5 20 20 17.5 20 14C20 8 14 2 14 2Z;M14 1C14 1 7 7 7 14C7 18 10 21 14 21C18 21 21 18 21 14C21 7 14 1 14 1Z;M14 2C14 2 8 8 8 14C8 17.5 10.5 20 14 20C17.5 20 20 17.5 20 14C20 8 14 2 14 2Z" dur="1.2s" repeatCount="indefinite" />
    </path>
    <path d="M14 8C14 8 11 11 11 14.5C11 16.5 12.5 18 14 18C15.5 18 17 16.5 17 14.5C17 11 14 8 14 8Z" fill="#fbbf24" opacity="0.85">
      <animate attributeName="opacity" values="0.85;0.4;0.85" dur="0.9s" repeatCount="indefinite" />
    </path>
  </svg>
);

/* ─── AnimNum ─── */
const AnimNum = ({ value }) => {
  const [display, setDisplay] = useState(0);
  const start = useRef(null);
  useEffect(() => {
    const from = display;
    start.current = null;
    const go = (ts) => {
      if (!start.current) start.current = ts;
      const p = Math.min((ts - start.current) / 700, 1);
      setDisplay(Math.round(from + (value - from) * (1 - Math.pow(1 - p, 3))));
      if (p < 1) requestAnimationFrame(go);
    };
    requestAnimationFrame(go);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return <span>{display}</span>;
};

/* ─── Streak Circle ─── */
const StreakCircle = ({ streak, isDark, size = 108 }) => {
  const stroke = Math.max(4, Math.round(size * 0.065));
  const r = (size - stroke * 2) / 2;
  const circ = 2 * Math.PI * r;
  const weekDay = streak % 7;
  const pct = streak === 0 ? 0 : weekDay === 0 ? 100 : (weekDay / 7) * 100;
  const flameSize = Math.round(size * 0.24);
  const numFont = Math.round(size * 0.205);
  const unitFont = Math.max(8, Math.round(size * 0.093));
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ position: 'absolute', transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={isDark ? '#2d2825' : '#e8e4df'} strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#ea580c" strokeWidth={stroke}
          strokeDasharray={`${(pct / 100) * circ} ${circ}`} strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 0.9s cubic-bezier(0.22,1,0.36,1)', filter: 'drop-shadow(0 0 4px rgba(234,88,12,0.5))' }} />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <FlameIcon size={flameSize} />
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 2, marginTop: 2 }}>
          <span style={{ fontSize: numFont, fontWeight: 900, color: isDark ? '#fafaf9' : '#1c1917', lineHeight: 1, letterSpacing: '-0.03em' }}>
            <AnimNum value={streak} />
          </span>
          <span style={{ fontSize: unitFont, color: '#a8a29e', fontWeight: 700 }}>d</span>
        </div>
      </div>
    </div>
  );
};

const getStreakCopy = (streak) => {
  if (streak === 0) return { headline: 'Start your streak today', sub: 'Complete one exercise to light the flame.' };
  if (streak < 3) return { headline: `Day ${streak} — back again`, sub: 'Momentum is building.' };
  if (streak < 7) return { headline: `Day ${streak} — locked in 🔥`, sub: `${7 - streak} days to your first weekly badge.` };
  if (streak === 7) return { headline: 'Week 1 complete 🏆', sub: 'Seven straight days. Badge earned.' };
  if (streak < 14) return { headline: `Day ${streak} — unstoppable`, sub: `Week 2 badge in ${14 - streak} days.` };
  if (streak < 30) return { headline: `Day ${streak} — elite tier`, sub: `${30 - streak} days to the legend badge.` };
  return { headline: `Day ${streak} — legend`, sub: 'Consistency like this is rare.' };
};

/* ══════════════════════════════════════════════════════
   WEEKLY PROGRESS — compact 7-segment "health bar" (Mon–Sun)
   Sits at 1/4 width next to Recent Activity. Each segment is filled if that day
   had ≥1 completed exercise, empty otherwise. Plus the "{n} this week" total.
   Source: completedExercises[].completed_at (user_progress). activity_log is
   empty/unwritten so it's not usable. No week-over-week delta: user_progress is
   upserted (completed_at = last completion), so a retried old exercise would
   distort a trend number — segments + this-week total only.
══════════════════════════════════════════════════════ */
const WEEK_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const WeeklyProgress = ({ completedExercises, isDark }) => {
  const card = isDark ? '#1c1917' : '#fff';
  const border = isDark ? '#2d2825' : '#e8e4df';
  const head = isDark ? '#fafaf9' : '#1c1917';
  const muted = isDark ? '#78716c' : '#a8a29e';
  const track = isDark ? '#2d2825' : '#f0ece7';

  const now = new Date();
  const dow = now.getDay();                       // 0=Sun … 6=Sat
  const toMonday = dow === 0 ? -6 : 1 - dow;
  const monday = new Date(now); monday.setHours(0, 0, 0, 0); monday.setDate(now.getDate() + toMonday);
  const dayStart = Array.from({ length: 7 }, (_, i) => { const d = new Date(monday); d.setDate(monday.getDate() + i); return d.getTime(); });
  const todayIdx = dow === 0 ? 6 : dow - 1;

  const counts = new Array(7).fill(0);
  (completedExercises || []).forEach((ex) => {
    if (!ex?.completed_at) return;
    const t = new Date(ex.completed_at).getTime();
    for (let i = 0; i < 7; i++) { if (t >= dayStart[i] && t < dayStart[i] + 86400000) { counts[i]++; break; } }
  });
  const total = counts.reduce((a, b) => a + b, 0);

  return (
    <div style={{ background: card, border: `1.5px solid ${border}`, borderRadius: T.radius['2xl'], padding: '18px 18px', height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 14 }}>
        <BarChart3 size={12} color="#ea580c" />
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9.5, fontWeight: 800, color: muted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Weekly</span>
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 30, fontWeight: 900, color: head, lineHeight: 1, letterSpacing: '-0.03em' }}>{total}</div>
        <div style={{ fontSize: 11, color: muted, marginTop: 3 }}>this week</div>
      </div>

      {/* 7-segment health bar — filled = that day had a completed exercise */}
      <div style={{ marginTop: 'auto' }}>
        <div style={{ display: 'flex', gap: 3, marginBottom: 5 }}>
          {counts.map((c, i) => (
            <div key={i} title={`${WEEK_LABELS[i]}: ${c} exercise${c === 1 ? '' : 's'}`} style={{
              flex: 1, height: 10, borderRadius: 5,
              background: c > 0 ? '#ea580c' : track,
              boxShadow: i === todayIdx ? `0 0 0 2px ${c > 0 ? 'rgba(234,88,12,0.28)' : (isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.10)')}` : 'none',
              transition: 'background 0.4s ease',
            }} />
          ))}
        </div>
        <div style={{ display: 'flex', gap: 3 }}>
          {WEEK_LABELS.map((l, i) => (
            <span key={i} style={{ flex: 1, textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 8, fontWeight: i === todayIdx ? 900 : 600, color: i === todayIdx ? '#ea580c' : muted }}>{l}</span>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   ACTIVITY CARD
══════════════════════════════════════════════════════ */
const ActivityCard = ({ items, isDark }) => {
  const card = isDark ? '#1c1917' : '#fff';
  const border = isDark ? '#2d2825' : '#e8e4df';
  const head = isDark ? '#fafaf9' : '#1c1917';
  const muted = isDark ? '#78716c' : '#a8a29e';

  const display = items.length > 0 ? items : [{
    icon: '🎉', iconBg: '#fef3c7',
    title: 'Welcome to Castle Atlas!',
    meta: 'Your journey starts here.',
    score: null, timeAgo: null,
  }];

  return (
    <div style={{ background: card, border: `1.5px solid ${border}`, borderRadius: T.radius['2xl'], padding: '20px 22px', height: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <Clock size={13} color="#ea580c" />
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 800, color: muted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Recent Activity</span>
      </div>
      <div>
        {display.slice(0, 5).map((item, i) => (
          <div key={i} style={{
            display: 'flex', gap: 10, alignItems: 'flex-start',
            paddingBottom: i < display.length - 1 ? 11 : 0,
            borderBottom: i < display.length - 1 ? `1px solid ${border}` : 'none',
            marginBottom: i < display.length - 1 ? 11 : 0,
          }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, background: isDark ? '#2d2825' : (item.iconBg || '#f5f2ef'), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>{item.icon}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: head, margin: '0 0 2px', lineHeight: 1.3 }}>{item.title}</p>
              <p style={{ fontSize: 10, color: muted, margin: 0 }}>{item.timeAgo || item.meta}</p>
            </div>
            {item.score != null && (
              <div style={{ background: isDark ? 'rgba(234,88,12,0.12)' : '#fff7ed', border: `1px solid ${isDark ? 'rgba(234,88,12,0.25)' : '#fed7aa'}`, borderRadius: 5, padding: '2px 6px', fontSize: 10, fontWeight: 800, color: '#ea580c', flexShrink: 0 }}>{item.score}/100</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   SHARE CARD
══════════════════════════════════════════════════════ */
const ShareCard = ({ streak, isDark }) => {
  const [dismissed, setDismissed] = useState(() => { try { return localStorage.getItem('ca_streak_share_dismissed') === 'true'; } catch { return false; } });
  const [socialIdx, setSocialIdx] = useState(0);
  useEffect(() => { const id = setInterval(() => setSocialIdx((i) => (i + 1) % SOCIALS.length), 10000); return () => clearInterval(id); }, []);
  if (dismissed || streak < 3) return null;

  const shareText = `🔥 ${streak}-day learning streak on Castle Atlas! Building real skills every day → castleatlas.co`;
  const card = isDark ? '#1c1917' : '#fff';
  const border = isDark ? '#2d2825' : '#e8e4df';
  const head = isDark ? '#fafaf9' : '#1c1917';
  const muted = isDark ? '#78716c' : '#a8a29e';

  return (
    <div style={{ background: card, border: `1.5px solid ${border}`, borderRadius: T.radius.xl, padding: '18px 20px', position: 'relative' }}>
      <button onClick={() => { try { localStorage.setItem('ca_streak_share_dismissed', 'true'); } catch {} setDismissed(true); }}
        aria-label="Dismiss"
        style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', cursor: 'pointer', color: muted, padding: 3 }}>
        <X size={13} />
      </button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <span style={{ fontSize: 18 }}>🔥</span>
        <div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, fontWeight: 800, color: '#ea580c', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 1 }}>Share Your Streak</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: head }}>{streak} days — let the world know</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {SOCIALS.map((s, i) => (
          <a key={s.id} href={s.url(shareText)} target="_blank" rel="noopener noreferrer"
            style={{
              flex: i === socialIdx ? '1 1 auto' : '0 0 auto',
              padding: i === socialIdx ? '9px 14px' : '9px 11px',
              background: i === socialIdx ? s.bg : (isDark ? '#292524' : '#f5f2ee'),
              color: i === socialIdx ? s.color : muted,
              border: 'none', borderRadius: T.radius.md,
              fontWeight: 700, fontSize: 12, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              textDecoration: 'none', transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)', whiteSpace: 'nowrap',
              minHeight: 38,
            }}>
            {s.icon}
            {i === socialIdx && <span>{s.label}</span>}
          </a>
        ))}
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   SKELETON
══════════════════════════════════════════════════════ */
/* ══════════════════════════════════════════════════════
   MAIN DASHBOARD
══════════════════════════════════════════════════════ */
const Dashboard = () => {
  usePageTitle('Dashboard — Castle Atlas');
  const { user, profile, loading } = useAuth();
  const { streak } = useStreak();
  const { completedExercises = [], totalCompleted = 0, loading: progressLoading } = useProgress();
  const { skills, loading: skillsLoading, error: skillsError } = useSkills();
  const navigate = useNavigate();

  const [activityLog, setActivityLog] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDark, setIsDark] = useState(() =>
    typeof window !== 'undefined' && document.documentElement.classList.contains('dark')
  );

  const isAdminUser = isAdmin(user?.email);

  useEffect(() => {
    const obs = new MutationObserver(() => setIsDark(document.documentElement.classList.contains('dark')));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  useEffect(() => { if (!loading && !user) navigate('/login'); }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.from('activity_log').select('*').eq('user_id', user.id).order('created_at', { ascending: false }).limit(5);
        if (!cancelled && data) setActivityLog(data);
      } catch { if (!cancelled) setError('Unable to load activity.'); }
      finally { if (!cancelled) setActivityLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const dataReady = !loading && !progressLoading && !activityLoading && !skillsLoading;
  if (!dataReady) {
    return <SkillLoadingScreen iconType="logo" iconAlt="Castle Atlas" loadingText="dashboard" />;
  }
  if (!user) return null;

  const firstName = profile?.full_name?.split(' ')[0] || 'there';
  const streakCopy = getStreakCopy(streak);

  const progressFor = (id) => {
    const done = completedExercises.filter((ex) => ex.skill_id === id);
    const last = done.length > 0
      ? done.reduce((l, ex) => new Date(ex.completed_at) > new Date(l.completed_at) ? ex : l).completed_at
      : null;
    return { done: done.length, last };
  };

  const actionRoute = (skill, done) => {
    if (done > 0) return EXERCISE_ROUTES[skill.id] || skill.route || '/skills';
    return skill.route || skill.landing_route || '/skills';
  };

  /* Live skills, gated: DB status:'live' plus the existing admin-only
     rule for copywriting (not itself expressed in the DB row). */
  const liveSkills = skills
    .filter((s) => s.status === 'live' && (!ADMIN_ONLY_SKILL_IDS.has(s.id) || isAdminUser))
    .map((s) => {
      const p = progressFor(s.id);
      return { ...s, done: p.done, last: p.last, pct: s.exercises > 0 ? Math.round((p.done / s.exercises) * 100) : 0 };
    });

  // Resolved up front (not inline in the MasonryGrid render prop) so its
  // index (0..N-1) is stable and matches what MasonryGrid measures/
  // positions — a skill with no registry card just never gets a slot.
  const skillCards = liveSkills
    .map((skill) => ({ skill, Card: getSkillCard(skill.card_shape) }))
    .filter(({ Card }) => Card);

  /* Single, deterministic resume CTA for the streak banner */
  const withActivity = liveSkills.filter((s) => s.last).sort((a, b) => new Date(b.last) - new Date(a.last));
  const nextSkill = withActivity[0] || liveSkills.find((s) => s.done === 0) || liveSkills[0];
  const nextHref = nextSkill ? actionRoute(nextSkill, nextSkill.done) : '/skills';
  const nextLabel = nextSkill ? (nextSkill.done > 0 ? `Continue ${nextSkill.name}` : `Start ${nextSkill.name}`) : 'Explore skills';

  const activityItems = activityLog.map((item) => ({
    icon: item.type === 'exercise_complete' ? '⚡' : item.type === 'streak_milestone' ? '🔥' : '🎯',
    iconBg: item.type === 'exercise_complete' ? '#fff7ed' : '#fef3c7',
    title: item.type === 'exercise_complete'
      ? `Completed · ${item.skill_id || 'AI Prompting'}`
      : item.type === 'streak_milestone' ? `${streak}-day streak!` : (item.metadata?.description || item.type),
    meta: new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }),
    score: item.score ?? null,
  }));

  const headColor = isDark ? '#fafaf9' : '#1c1917';
  const mutedColor = isDark ? '#78716c' : '#a8a29e';
  const dashColor = isDark ? '#57534e' : '#d4cdc7';
  const pageBg = isDark ? '#0e0d0c' : '#faf9f7';
  const skillCount = liveSkills.length;

  return (
    <div style={{ maxWidth: '100%', minHeight: '100vh', backgroundColor: pageBg, transition: 'background-color 0.3s ease' }}>
      <style>{`
        @keyframes fadeUp  { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin    { to{transform:rotate(360deg)} }
        @keyframes pulse-dot{ 0%,100%{box-shadow:0 0 0 0 rgba(34,197,94,0.4)} 50%{box-shadow:0 0 0 4px rgba(34,197,94,0)} }
        .du1{animation:fadeUp 0.4s ease-out both}
        .du2{animation:fadeUp 0.4s ease-out 0.08s both}
        .du3{animation:fadeUp 0.4s ease-out 0.18s both}
        .du4{animation:fadeUp 0.4s ease-out 0.28s both}

        .streak-hero { display:flex; align-items:center; gap:20px; flex-wrap:wrap; }
        .streak-hero-info { flex:1 1 220px; min-width:220px; }
        .streak-hero-stats { flex:0 0 auto; }
        @media(max-width:520px){
          .streak-hero { justify-content:center; text-align:center; }
          .streak-hero-info { text-align:center; }
        }

        .dash-row-2 { display:grid; grid-template-columns:1fr 1fr; gap:20px; align-items:stretch; }
        @media(max-width:820px){ .dash-row-2{ grid-template-columns:1fr; } }

        /* Recent Activity (3) + Weekly Progress (1) share the row's right cell. */
        .dash-activity-weekly { display:grid; grid-template-columns:3fr 1fr; gap:16px; align-items:stretch; }
        @media(max-width:560px){ .dash-activity-weekly{ grid-template-columns:1fr; } }

        .dash-secondary-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(260px,1fr)); gap:16px; }

        @media(prefers-reduced-motion:reduce){
          .du1,.du2,.du3,.du4{ animation:none!important; }
          [style*="pulse-dot"]{ animation:none!important; }
        }
      `}</style>

      <div className="rail-clearance" style={{ padding: '28px 24px 72px' }}>

        {(error || skillsError) && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: '10px 16px', marginBottom: 16, color: '#dc2626', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
            ⚠️ {error || skillsError}
            <button onClick={() => window.location.reload()} style={{ marginLeft: 'auto', background: 'none', border: 'none', fontWeight: 700, cursor: 'pointer', color: '#dc2626' }}>Refresh →</button>
          </div>
        )}

        {/* ── HEADER — Atlas Plates shell ── */}
        <div className="du1" style={{ position: 'relative', marginBottom: 28, paddingTop: 8 }}>
          <div style={{ position: 'absolute', top: -8, left: -24, right: -24, height: 140, overflow: 'hidden', pointerEvents: 'none' }}>
            <ContourBG isDark={isDark} size="compact" opacity={0.9} />
          </div>
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: '#ea580c', letterSpacing: '0.14em' }}>BASE CAMP</span>
              <span style={{ width: 22, height: 1, background: dashColor, display: 'inline-block' }} />
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: mutedColor, letterSpacing: '0.14em', textTransform: 'uppercase' }}>Dashboard</span>
            </div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '4px 12px', borderRadius: 99, marginBottom: 12, background: 'rgba(234,88,12,0.06)', border: '1px solid rgba(234,88,12,0.14)' }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', animation: 'pulse-dot 2s ease-in-out infinite' }} />
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700, color: '#ea580c' }}>{skillCount} skill{skillCount > 1 ? 's' : ''} active</span>
            </div>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.6rem,4.5vw,2rem)', fontWeight: 900, letterSpacing: '-0.03em', color: headColor, margin: '0 0 6px', lineHeight: 1.15 }}>
              Pick up where you left off, {firstName}
            </h1>
            <p style={{ fontSize: 14, color: mutedColor, margin: 0, maxWidth: 460, lineHeight: 1.6 }}>
              {streak > 0 ? `You're on a ${streak}-day streak. Keep the momentum.` : 'Start building skills that actually pay.'}
            </p>
          </div>
        </div>

        {/* ── ROW 1: STREAK | ACTIVITY — always visible ── */}
        <div className="du2 dash-row-2" style={{ marginBottom: 20 }}>
          <div className="streak-hero" style={{
            position: 'relative', overflow: 'hidden',
            background: isDark ? 'linear-gradient(145deg,#1a0f08 0%,#21130a 60%,#2a1a0e 100%)' : 'linear-gradient(145deg,#fff7ed 0%,#ffedd5 60%,#fed7aa 100%)',
            border: `1.5px solid ${isDark ? '#5c3d1e' : '#fdba74'}`,
            borderRadius: T.radius.xl, padding: '22px 24px',
          }}>
            {/* Primary column, top→bottom: eyebrow → Resume/Start headline → CTA on its own line */}
            <div className="streak-hero-info">
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#ea580c', marginBottom: 8 }}>
                {streak > 0 ? `${streak}-day streak` : 'Base camp'}
              </div>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 900, color: headColor, margin: '0 0 4px', lineHeight: 1.2, letterSpacing: '-0.02em' }}>
                {nextSkill ? (nextSkill.done > 0 ? `Resume: ${nextSkill.name}` : `Start: ${nextSkill.name}`) : 'Explore skills'}
              </h2>
              <p style={{ fontSize: 12, color: mutedColor, margin: '0 0 16px' }}>{streakCopy.sub}</p>
              <a href={nextHref} onClick={(e) => { e.preventDefault(); navigate(nextHref); }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7, minHeight: 42, boxSizing: 'border-box',
                  padding: '11px 20px', background: nextSkill?.color_primary || '#ea580c', color: '#fff',
                  border: 'none', borderRadius: T.radius.md, fontWeight: 800, fontSize: 13,
                  cursor: 'pointer', boxShadow: `0 4px 14px ${nextSkill?.color_glow || 'rgba(234,88,12,0.35)'}`,
                  transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)', textDecoration: 'none', whiteSpace: 'normal',
                }}>
                {nextLabel} <ArrowRight size={14} />
              </a>
            </div>
            {/* Supporting stat: shrunken streak circle + total done — no longer competes with the CTA */}
            <div className="streak-hero-stats" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <StreakCircle streak={streak} isDark={isDark} size={72} />
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, padding: '8px 14px', background: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.5)', borderRadius: T.radius.md, border: isDark ? '1px solid #3d2a1a' : '1px solid rgba(234,88,12,0.12)' }}>
                <span style={{ fontSize: 14, fontWeight: 800, color: headColor }}>{totalCompleted}</span>
                <span style={{ fontSize: 8, fontWeight: 700, color: mutedColor, textTransform: 'uppercase', letterSpacing: '0.08em' }}>done</span>
              </div>
            </div>
          </div>
          {/* Recent Activity (3 cols) + Weekly Progress (1 col) side by side */}
          <div className="dash-activity-weekly">
            <ActivityCard items={activityItems} isDark={isDark} />
            <WeeklyProgress completedExercises={completedExercises} isDark={isDark} />
          </div>
        </div>

        {/* ── ROW 2: EVERY LIVE SKILL — registry cards via getSkillCard,
               masonry layout (components/MasonryGrid.jsx), same technique
               as Skills.jsx so they render identically. Cards stay flat
               siblings in skillCards' order, so tab order is just that
               document order regardless of which column each lands in.
               Card components don't forward refs, so each gets a plain div
               wrapper to carry MasonryGrid's ref/position style — the
               previous grid rendered <Card> directly with no wrapper. ── */}
        <div className="du3" style={{ marginBottom: 20 }}>
          <MasonryGrid itemCount={skillCards.length}>
            {(i, style, ref) => {
              const { skill, Card } = skillCards[i];
              return (
                /* Staggered arrival: the cards used to snap in all at
                   once the moment the loader left. Delay is per-index and
                   capped by the .sls-stagger rules in index.css. */
                <div
                  key={skill.id}
                  ref={ref}
                  className="sls-card-enter"
                  style={{ ...style, animationDelay: `${60 + i * 70}ms` }}
                >
                  <Card
                    skill={skill}
                    progress={skill.done}
                    isDark={isDark}
                    mode="dashboard"
                    onAction={() => navigate(actionRoute(skill, skill.done))}
                  />
                </div>
              );
            }}
          </MasonryGrid>
        </div>

        {/* ── "Vote for what's next" — shared with Skills.jsx (components/
               VoteForNextSection.jsx) so both pages render the same
               heading + Technical Translation announcement. variant="link"
               means Dashboard shows a one-line pointer to the leaderboard
               instead of the full chip list — the live chips and their
               single Realtime subscription live on Skills only. ── */}
        <div className="du3" style={{ marginTop: 40 }}>
          <VoteForNextSection
            skills={skills}
            isDark={isDark}
            headColor={headColor}
            mutedColor={mutedColor}
            dashColor={dashColor}
            source="dashboard_page_vote"
            variant="link"
          />
        </div>

        {/* ── ROW 3: SECONDARY TIER — share (Vote cards removed, referral program removed) ── */}
        <div className="du4" style={{ marginTop: 40 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 600, color: mutedColor, letterSpacing: '0.14em' }}>MORE WAYS TO KEEP MOMENTUM</span>
            <span style={{ width: 22, height: 1, background: dashColor, display: 'inline-block' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <ShareCard streak={streak} isDark={isDark} />
          </div>
        </div>

      </div>
    </div>
  );
};

export default Dashboard;